import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { reactPaths } from 'root/pathnames';
import { User } from 'types/user';
import { hasPrivilege } from 'utils/authFunctions.utils';

let globalTransportCloudLeave: (() => void) | null = null;

export const setGlobalTransportCloudLeave = (callback: (() => void) | null) => {
  globalTransportCloudLeave = callback;
};

const useProvideAuth = () => {
  const [user, setUser] = useState<User | null>(null);
  const [allowedReplacements, setAllowedReplacements] = useState<number[]>([]);
  const [allowedReplacementsByCode, setAllowedReplacementsByCode] = useState<
    string[]
  >([]);
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();

  const clearUser = () => {
    setUser(null);
    setAllowedReplacements([]);
    setAllowedReplacementsByCode([]);
  };

  const checkAuth = useCallback(
    ({
      successCallback,
      failureCallback,
    }: {
      successCallback?: VoidFunction;
      failureCallback?: VoidFunction;
    } = {}) =>
      fetch('/backend/service/context')
        .then((res) => {
          const locationHeader = res.headers.get('location');
          if (locationHeader) {
            const responseURL = new URL(locationHeader);
            const responsePathname = responseURL.pathname;
            if (responsePathname === reactPaths.acceptances) {
              return { redirect: reactPaths.acceptances };
            }
          }
          return res.json();
        })
        .then((data) => {
          if (data.redirect) {
            data?.redirect &&
              navigate(reactPaths.acceptances, { replace: true });
            return;
          }
          if (data.status === 'OK') {
            setUser(data.result?.currentUser);
            setAllowedReplacements(
              data.result?.allowedReplacements?.map((user: User) => user.id),
            );
            setAllowedReplacementsByCode(
              data.result?.allowedReplacementsByCode?.map(
                (user: User) => user.code,
              ),
            );

            successCallback && successCallback();

            return data.result?.currentUser as User;
          } else {
            clearUser();
            failureCallback && failureCallback();
            return null;
          }
        })
        .finally(() => setLoading(false)),
    [],
  );

  const logoutUser = async (alert?: string) => {
    if (globalTransportCloudLeave) {
      globalTransportCloudLeave();
    }

    await fetch('/backend/logout.do');
    const params = alert && new URLSearchParams({ alert });
    await checkAuth({
      failureCallback: () =>
        navigate({
          pathname: reactPaths.login,
          search: params?.toString(),
        }),
    });
  };

  // copied from legacy app
  const isAuthorized = (privilege?: null | { name: string }) =>
    !!user && (!privilege || hasPrivilege(privilege, user));

  // copied from legacy app
  const hasViewPermission = (requiredRoles?: null | { name: string }[]) => {
    if (!requiredRoles || requiredRoles.length === 0) return true;
    return requiredRoles.some(isAuthorized);
  };

  return {
    user,
    allowedReplacements,
    allowedReplacementsByCode,
    loading,
    setLoading,
    checkAuth,
    logoutUser,
    signOut: clearUser,
    isAuthorized,
    hasViewPermission,
  };
};

const authContext = createContext<
  ReturnType<typeof useProvideAuth> | undefined
>(undefined);

export const useAuth = () => {
  const context = useContext(authContext);

  if (context === undefined) {
    throw new Error('useAuth must be used within a AuthProvider');
  }

  return context;
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const auth = useProvideAuth();
  return <authContext.Provider value={auth}>{children}</authContext.Provider>;
};
